import { DollarSign, User, Radar, type LucideIcon } from "lucide-react"
import { demoProfilePictures, DEMO_USER_IDS } from "src/mockData/demoData"

export const DEMO_DOCUMENT_VERSIONS = [
  { id: "v3", label: "v3 (Current)", date: "Jan 7, 2025", author: "You", isCurrent: true },
  { id: "v2", label: "v2", date: "Dec 20, 2024", author: "Legal Team", isCurrent: false },
  { id: "v1", label: "v1 (Original)", date: "Dec 1, 2024", author: "You", isCurrent: false },
]

export const DEMO_DEAL_SUMMARY = {
  description: "Multi-year recording agreement covering 2 albums with options for a third. Includes advance payments, royalty splits, and territory rights for worldwide distribution.",
  startDate: "Dec 1, 2024",
  targetClose: "Feb 15, 2025",
  dealValue: "$150,000",
  dealType: "Record Label",
  territory: "Worldwide",
  duration: "3 years + 1 option",
  daysElapsed: 54,
  totalDays: 77,
  progressPercent: 70,
  albumCount: 2,
  royaltySplit: "50/50",
  advancePercent: 33,
}

export const DEAL_BREAKDOWN = [
  { label: "Advance", percent: 33, value: "$50,000", color: "bg-violet-500" },
  { label: "Royalties", percent: 45, value: "$67,500", color: "bg-emerald-500" },
  { label: "Publishing", percent: 22, value: "$32,500", color: "bg-amber-500" },
]

export type KeyTermId = "term-1" | "term-2" | "term-3"
export type KeyTermChartKind = "advance_progress" | "ownership_split" | "risk_horizon" | "holistic_analysis"

export interface KeyDealTerm {
  id: KeyTermId
  type: "money" | "ownership" | "risk" | "analysis"
  title: string
  value: string
  detail: string
  icon: LucideIcon
}

type AdvanceProgressChartData = {
  kind: "advance_progress"
  progressPercent: number
  totalAdvance: number
  installmentCount: number
}

type OwnershipSplitSegment = {
  name: string
  value: number
  color: string
}

type OwnershipSplitChartData = {
  kind: "ownership_split"
  segments: OwnershipSplitSegment[]
}

type RiskHorizonPoint = {
  label: string
  years: number
  color: string
}

type RiskHorizonChartData = {
  kind: "risk_horizon"
  points: RiskHorizonPoint[]
}

type HolisticAnalysisAxis = {
  subject: string
  score: number
  fullMark: number
}

type HolisticAnalysisChartData = {
  kind: "holistic_analysis"
  axes: HolisticAnalysisAxis[]
}

export type KeyTermChartData =
  | AdvanceProgressChartData
  | OwnershipSplitChartData
  | RiskHorizonChartData
  | HolisticAnalysisChartData

export const KEY_DEAL_TERMS: KeyDealTerm[] = [
  { id: "term-1", type: "money", title: "Advance Payment", value: "$150,000", detail: "Paid in 3 installments", icon: DollarSign },
  { id: "term-2", type: "ownership", title: "Master Ownership", value: "50/50", detail: "Reverts after 7 years", icon: User },
  { id: "term-3", type: "analysis", title: "Holistic Analysis", value: "Strong", detail: "5-point advisory assessment", icon: Radar },
]

export const KEY_TERM_CHART_DATA: Partial<Record<KeyTermId, KeyTermChartData>> = {
  "term-1": {
    kind: "advance_progress",
    progressPercent: 33,
    totalAdvance: 150000,
    installmentCount: 3,
  },
  "term-2": {
    kind: "ownership_split",
    segments: [
      { name: "Artist", value: 50, color: "#6d7dff" },
      { name: "Label", value: 50, color: "#34d399" },
    ],
  },
  "term-3": {
    kind: "holistic_analysis",
    axes: [
      { subject: "Financial", score: 5, fullMark: 5 },
      { subject: "Legal", score: 4, fullMark: 5 },
      { subject: "Marketing", score: 4, fullMark: 5 },
      { subject: "Scheduling", score: 3, fullMark: 5 },
      { subject: "Operations", score: 4, fullMark: 5 },
    ],
  },
}

export const EXAMPLE_ACTIVITY = [
  { id: "activity-1", user: "Adam Silver", action: "commented", content: "The royalty split looks good, but we should negotiate the territory exclusivity clause.", timestamp: "2 hours ago", avatar: demoProfilePictures[DEMO_USER_IDS.ADAM_SILVER] },
  { id: "activity-2", user: "You", action: "uploaded", content: "Amendment #1", timestamp: "5 hours ago", avatar: undefined },
  { id: "activity-3", user: "Roger Goodell", action: "approved", content: "Master Agreement terms", timestamp: "1 day ago", avatar: demoProfilePictures[DEMO_USER_IDS.ROGER_GOODELL] },
  { id: "activity-4", user: "Adam Silver", action: "commented", content: "Let's schedule a call to discuss the advance payment structure.", timestamp: "2 days ago", avatar: demoProfilePictures[DEMO_USER_IDS.ADAM_SILVER] },
  { id: "activity-5", user: "System", action: "created", content: "Deal created", timestamp: "Dec 1, 2024", avatar: undefined },
]
